import { useNavigate } from 'react-router-dom';

import { Button, ButtonGroup, Tooltip, useTheme } from '@mui/material';

import { Badge } from '@/components/TailwindComponents/base';
import { cn } from '@/components/TailwindComponents/lib/utils';

type NavigationButtonGroupProps = {
  tabs?: {
    label: string;
    path: string;
    disabled?: boolean;
    onClick?: () => void;
    count?: number;
    tooltip?: string;
  }[];
  activeTab?: string;
};

const NavigationButtonGroup = ({
  tabs,
  activeTab,
}: NavigationButtonGroupProps) => {
  if (!tabs) return null;
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <ButtonGroup
      sx={{
        backgroundColor: theme.palette.background.paper,
        marginY: 2,
        width: '100%',
      }}
      size="large"
      aria-label="Large button group"
    >
      {tabs &&
        tabs.map((tab) => {
          const button = (
            <Button
              type="button"
              disabled={tab.disabled}
              sx={{
                borderColor: 'transparent',
                borderRightColor: 'transparent !important',
                paddingX: 4,
                paddingY: 2,
                color:
                  activeTab !== tab.path
                    ? theme.palette.text.secondary
                    : theme.palette.getContrastText(theme.palette.error.main),
              }}
              variant={activeTab === tab.path ? 'contained' : 'text'}
              color="error"
              key={tab.path}
              onClick={() => {
                // If onClick is provided, use it; otherwise use navigate
                if (tab.onClick) {
                  tab.onClick();
                } else {
                  navigate(tab.path, { replace: true });
                }
              }}
            >
              {tab.label}
              {tab.count !== undefined && (
                <Badge
                  variant="secondary"
                  className={cn(
                    'ml-2 inline-flex h-5 w-5 min-w-5 shrink-0 aspect-square items-center justify-center rounded-full border-0 p-0 text-[11px] leading-none',
                    activeTab === tab.path
                      ? 'bg-white/20 text-white hover:bg-white/20'
                      : 'bg-red-500 text-white hover:bg-red-500',
                  )}
                >
                  {tab.count}
                </Badge>
              )}
            </Button>
          );

          if (!tab.tooltip) return button;

          // A disabled button suppresses pointer events, so the span wrapper
          // is what lets the tooltip fire.
          return (
            <Tooltip title={tab.tooltip} key={tab.path}>
              <span>{button}</span>
            </Tooltip>
          );
        })}
    </ButtonGroup>
  );
};

export default NavigationButtonGroup;
